import React, { lazy, Suspense, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Check,
  CheckCircle,
  Globe,
  Loader2,
  Shield,
  Sparkles,
  Zap,
  Crown,
  Users,
  X,
  Coins,
  Wand2,
  Box,
  Video,
  Layers,
  HardDrive,
  BarChart3
} from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import tierService, { Tier } from '../services/tierService';
import adminPricingService, { PricingPlanResponse } from '../services/adminPricingService';
import { WaitlistModal } from '../components/pricing/WaitlistModal';

import Navbar from '../components/layout/Navbar';
import DynamicCreditExplanation from '../components/pricing/DynamicCreditExplanation';
import WhatIsQuixocial from '../components/seo/WhatIsQuixocial';
import StructuredFAQ from '../components/seo/StructuredFAQ';
import { generalFAQs } from '../data/faqData';
import { useMobile } from '../hooks/useMobile';

// Lazy load heavy components
const CompletePipelineShowcase = lazy(() => import('../components/pipeline/CompletePipelineShowcase'));
const EnhancedContentShowcase = lazy(() => import('../components/landing/EnhancedContentShowcase'));
const SchedulingShowcase = lazy(() => import('../components/landing/SchedulingShowcase'));
const AnalyticsSection = lazy(() => import('../components/landing/AnalyticsSection'));
const FeaturesSection = lazy(() => import('../components/landing/FeaturesSection'));

// Testimonial Carousel Component with dynamic indicators
const TestimonialCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        const scrollLeft = scrollRef.current.scrollLeft;
        const cardWidth = scrollRef.current.offsetWidth * 0.85 + 16; // 85vw + gap
        const index = Math.round(scrollLeft / cardWidth);
        setActiveIndex(index);
      }
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const testimonials = [
    {
      text: "Quixocial helped us create 5× more content in less time, which directly increased our sales. It also saved us valuable time by eliminating the need for manual post publishing. It's the definition of working smart, not hard.",
      name: "A. El Masry",
      title: "CSO, Battireo",
      initial: "A",
      gradient: "from-blue-500 to-purple-600",
      bgGradient: "from-white to-blue-50/50",
      border: "border-blue-100/50"
    },
    {
      text: "As a small business owner, I was spending ridiculous amounts of time creating content and managing my social media instead of focusing on my actual work. Quixocial lifted a huge part of that burden.",
      name: "J. Blanc",
      title: "Founder, Kunst",
      initial: "J",
      gradient: "from-purple-500 to-pink-600",
      bgGradient: "from-white to-purple-50/50",
      border: "border-purple-100/50"
    },
    {
      text: "Quixocial cut our visual content costs, saved us hours on manual publishing and management, and streamlined our entire workflow. Honestly, it is a game changer!",
      name: "Dr. A. Samy",
      title: "Co-Founder, Bebek",
      initial: "A",
      gradient: "from-green-500 to-teal-600",
      bgGradient: "from-white to-green-50/50",
      border: "border-green-100/50"
    }
  ];

  return (
    <div className="md:hidden relative">
      <div
        ref={scrollRef}
        className="flex overflow-x-auto space-x-4 pb-6 pt-4 px-2 -mx-2 snap-x snap-mandatory scrollbar-hide"
      >
        {testimonials.map((testimonial, index) => (
          <div key={index} className="flex-shrink-0 w-[85vw] snap-center">
            <div className={`relative bg-gradient-to-br ${testimonial.bgGradient} rounded-3xl p-6 shadow-xl border ${testimonial.border} h-full`}>
              <div className="flex mb-3">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-800 text-base mb-6 leading-relaxed font-medium">
                {testimonial.text}
              </p>
              <div className="flex items-center pt-4 border-t border-gray-200">
                <div className={`w-14 h-14 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center text-white font-bold text-xl mr-3 shadow-md`}>
                  {testimonial.initial}
                </div>
                <div>
                  <div className="font-bold text-base text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dynamic Scroll Indicators */}
      <div className="flex justify-center mt-4 space-x-2">
        {testimonials.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'bg-blue-600 w-6' : 'bg-gray-300'
              }`}
          />
        ))}
      </div>
    </div>
  );
};


// Icon mapping function
const getIconComponent = (iconName?: string) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    Users,
    Zap,
    Crown,
    Shield,
    Sparkles,
    Coins,
    Wand2,
    Layers,
    Box,
    Video,
    HardDrive
  };

  // Default to Users if no icon specified or icon not found
  const IconComponent = iconMap[iconName || 'Users'] || Users;
  return <IconComponent className="w-6 h-6 text-white" />;
};



const LandingPage: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const isMobile = useMobile();
  const [isYearly, setIsYearly] = useState(false);
  const [waitlistModal, setWaitlistModal] = useState({
    isOpen: false,
    tierName: '',
    featureName: ''
  });

  // Track landing page view
  React.useEffect(() => {
    import('../lib/analytics').then(({ trackEvent }) => {
      trackEvent('landing_page_view');
    });
    import('../lib/metaPixel').then(({ trackMetaEvent }) => {
      trackMetaEvent('ViewContent', { content_name: 'Landing Page' });
    });
  }, []);

  // Preload critical images
  React.useEffect(() => {
    const criticalImages = [
      '/images/landing/original.png',
      '/images/landing/output.png',
      '/images/landing/brandify-original.png'
    ];

    criticalImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Fallback tiers in case API fails
  const fallbackTiers: Tier[] = [
    {
      id: 'free',
      name: 'free',
      display_name: 'Free',
      description: 'Perfect for getting started',
      price_monthly: 0,
      price_yearly: 0,
      is_active: true,
      sort_order: 1,
      signup_credits: 10,
      monthly_credits: 5,
      bonus_credits: 15,
      bonus_start_date: '2024-12-01T00:00:00Z',
      bonus_end_date: '2024-12-31T23:59:59Z',
      bonus_description: 'Launch Special',
      bonus_is_active: true,
      button_text: undefined,
      button_url: undefined,
      is_button_enabled: true,
      roi_text_1: 'Save $200/month vs hiring designers',
      roi_text_2: undefined,
      show_roi_texts: true,
      created_at: '',
      updated_at: ''
    },
    {
      id: 'pro',
      name: 'pro',
      display_name: 'Pro',
      description: 'For growing businesses',
      price_monthly: 29.99,
      price_yearly: 299.99,
      is_active: true,
      sort_order: 2,
      signup_credits: 50,
      monthly_credits: 100,
      bonus_credits: 50,
      bonus_start_date: '2024-12-01T00:00:00Z',
      bonus_end_date: '2024-12-31T23:59:59Z',
      bonus_description: 'Launch Special',
      bonus_is_active: true,
      button_text: undefined,
      button_url: undefined,
      is_button_enabled: true,
      roi_text_1: 'Save $2,000/month in content costs',
      roi_text_2: undefined,
      show_roi_texts: true,
      created_at: '',
      updated_at: ''
    },
    {
      id: 'enterprise',
      name: 'enterprise',
      display_name: 'Enterprise',
      description: 'For large organizations',
      price_monthly: 99.99,
      price_yearly: 999.99,
      is_active: true,
      sort_order: 3,
      signup_credits: 200,
      monthly_credits: 500,
      bonus_credits: 0,
      bonus_start_date: undefined,
      bonus_end_date: undefined,
      bonus_description: undefined,
      bonus_is_active: false,
      button_text: undefined,
      button_url: undefined,
      is_button_enabled: true,
      roi_text_1: 'Scale without 10x team growth',
      roi_text_2: undefined,
      show_roi_texts: true,
      created_at: '',
      updated_at: ''
    }
  ];

  // Fetch tiers from API (public endpoint, no auth required)
  const { data: apiTiers, isLoading: tiersLoading } = useQuery<Tier[]>({
    queryKey: ['public-tiers', 'active'],
    queryFn: () => tierService.getPublicTiers(true),
    retry: 1,
    staleTime: 30 * 1000,
    cacheTime: 2 * 60 * 1000,
  });

  // Fetch dynamic pricing features (public endpoint, no auth required)
  const { data: pricingPlans, isLoading: featuresLoading } = useQuery<PricingPlanResponse>({
    queryKey: ['pricing-plans'],
    queryFn: adminPricingService.getPricingPlans,
    retry: 1,
    staleTime: 30 * 1000,
    cacheTime: 2 * 60 * 1000,
  });

  // Use API tiers (which include credits) and merge with pricing plans features if available - ensure it's always an array
  const tiers = Array.isArray(apiTiers) ? apiTiers : fallbackTiers;

  // Get features for a specific tier from dynamic pricing data
  const getTierFeatures = (tierId: string): Array<{ text: string, isIncluded: boolean, badgeText?: string, badgeColor?: string }> => {
    if (pricingPlans) {
      // Find the tier in pricing plans by matching ID or name
      const pricingTier = pricingPlans.tiers.find(t => t.id === tierId);
      if (pricingTier?.features) {
        // Convert feature objects to objects with text and isIncluded
        return pricingTier.features.map(feature => {
          if (typeof feature === 'string') {
            return { text: feature, isIncluded: true };
          }
          // Handle feature objects with display_text or pricing_feature.name
          const text = feature.display_text ||
            feature.pricing_feature?.name ||
            feature.name ||
            'Feature included';
          const isIncluded = feature.is_included !== false; // Default to true if not specified
          const badgeText = feature.badge_text;
          const badgeColor = feature.badge_color;
          return { text, isIncluded, badgeText, badgeColor };
        });
      }

      // If not found by ID, try to match by name
      const apiTier = tiers.find(t => t.id === tierId);
      if (apiTier) {
        const pricingTierByName = pricingPlans.tiers.find(t => t.name === apiTier.name);
        if (pricingTierByName?.features) {
          return pricingTierByName.features.map(feature => {
            if (typeof feature === 'string') {
              return { text: feature, isIncluded: true };
            }
            const text = feature.display_text ||
              feature.pricing_feature?.name ||
              feature.name ||
              'Feature included';
            const isIncluded = feature.is_included !== false; // Default to true if not specified
            const badgeText = feature.badge_text;
            const badgeColor = feature.badge_color;
            return { text, isIncluded, badgeText, badgeColor };
          });
        }
      }
    }

    // Fallback to hardcoded features if dynamic data not available
    const tierName = tiers.find(t => t.id === tierId)?.name || '';
    const fallbackFeatures: Record<string, Array<{ text: string, isIncluded: boolean }>> = {
      free: [
        { text: '2 connected social accounts', isIncluded: true },
        { text: '1 team member', isIncluded: true },
        { text: '5 AI generations per month', isIncluded: true },
        { text: 'Basic content scheduling', isIncluded: true },
        { text: 'Social analytics', isIncluded: false },
        { text: 'Community support', isIncluded: true }
      ],
      pro: [
        { text: '10 connected social accounts', isIncluded: true },
        { text: '5 team members', isIncluded: true },
        { text: '100 AI generations per month', isIncluded: true },
        { text: 'Advanced scheduling', isIncluded: true },
        { text: 'Social analytics', isIncluded: true },
        { text: 'Priority support', isIncluded: true },
        { text: 'Team collaboration', isIncluded: true }
      ],
      enterprise: [
        { text: 'Unlimited social accounts', isIncluded: true },
        { text: 'Unlimited team members', isIncluded: true },
        { text: '500 AI generations per month', isIncluded: true },
        { text: 'Social analytics', isIncluded: true },
        { text: 'Custom integrations', isIncluded: true },
        { text: 'Dedicated account manager', isIncluded: true },
        { text: 'Advanced security', isIncluded: true }
      ]
    };

    return fallbackFeatures[tierName] || [];
  };

  const formatPrice = (price?: number, tierName?: string) => {
    if (!price || price === 0) return 'Free';
    // Only show "Custom Pricing" if it's enterprise AND has no price set
    if (tierName === 'enterprise' && !price) return 'Coming Soon';
    return `$${price.toFixed(2)}`;
  };

  // Get limited features for landing page display (first 4 + count of remaining)
  const getLimitedTierFeatures = (tierId: string): {
    displayFeatures: Array<{ text: string, isIncluded: boolean, badgeText?: string, badgeColor?: string }>,
    remainingCount: number
  } => {
    const allFeatures = getTierFeatures(tierId);
    const displayFeatures = allFeatures.slice(0, 4);
    const remainingCount = Math.max(0, allFeatures.length - 4);

    return { displayFeatures, remainingCount };
  };

  const getDisplayPrice = (tier: any) => {
    if (isYearly && tier.price_yearly) {
      return Math.ceil(tier.price_yearly / 12);
    }
    if (tier.price_monthly) {
      return tier.price_monthly;
    }
    return null; // Return null instead of 0 for no pricing
  };

  const getPricePeriod = (tier: any) => {
    if (tier.name === 'free') return '';
    if (tier.name === 'enterprise') return '';
    return isYearly ? '/mo (billed yearly)' : '/mo';
  };

  const getYearlySavings = (tier: any) => {
    if (!tier.price_yearly || !tier.price_monthly || tier.price_monthly === 0) return 0;
    const monthlyTotal = tier.price_monthly * 12;
    const savings = monthlyTotal - tier.price_yearly;
    return Math.round((savings / monthlyTotal) * 100);
  };

  const getButtonText = (tier: any) => {
    // Use custom button text if available and button is enabled
    if (tier.is_button_enabled && tier.button_text) return tier.button_text;

    // Fallback to default logic - use consistent "Start Free" for all tiers
    return 'Start Free';
  };

  const getButtonLink = (tier: any) => {
    // Landing page: all buttons lead to pricing page
    return '/pricing';
  };

  const handleButtonClick = (tier: any, e: React.MouseEvent) => {
    // If button URL is set to "waitlist", open waitlist modal
    if (tier.button_url === 'waitlist') {
      e.preventDefault();
      setWaitlistModal({
        isOpen: true,
        tierName: tier.display_name,
        featureName: `${tier.display_name} Plan`
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 force-light-theme">
      <Navbar activePage="home" />

      {/* Hero Section - Two Column Layout */}
      <section className="relative pt-16 sm:pt-20 md:pt-24 lg:pt-32 pb-20 sm:pb-24 md:pb-28 lg:pb-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50" style={{ willChange: 'auto' }}></div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ willChange: 'auto' }}>
          <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-2xl opacity-20" style={{ animationDuration: '5s', background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.3), rgba(74, 41, 138, 0.3))', animation: 'pulse 5s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
          <div className="absolute top-40 right-20 w-40 h-40 rounded-full blur-3xl opacity-15" style={{ animationDelay: '2s', animationDuration: '6s', background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.25), rgba(219, 39, 119, 0.25))', animation: 'pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
          <div className="absolute bottom-32 left-20 w-28 h-28 rounded-full blur-2xl opacity-25" style={{ animationDelay: '1s', animationDuration: '5.5s', background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.35), rgba(59, 130, 246, 0.35))', animation: 'pulse 5.5s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
        </div>

        {/* Two Column Container */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px]">

            {/* Left Column - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left space-y-6 lg:space-y-8"
            >
              <div className="flex justify-center lg:justify-start">
                <Badge variant="secondary" className="px-4 py-2 text-sm font-bold shadow-lg backdrop-blur-sm" style={{ background: 'linear-gradient(to right, rgba(48, 179, 221, 0.1), rgba(48, 179, 221, 0.05), rgba(74, 41, 138, 0.1))', borderColor: 'rgba(48, 179, 221, 0.3)' }}>
                  <span style={{ background: 'linear-gradient(to right, #1e40af, #6b21a8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 'bold' }}>
                    Next-Gen Content Management
                  </span>
                </Badge>
              </div>

              <h1
                className="hero-title text-5xl sm:text-6xl lg:text-7xl leading-tight tracking-wide"
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '0.05em'
                }}
              >
                <span className="block mb-2" style={{
                  background: 'linear-gradient(135deg, #0369a1 0%, #1e40af 50%, #1e3a8a 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Brand-Intelligent Content
                </span>
                <span className="block mb-2" style={{
                  background: 'linear-gradient(135deg, #0e7490 0%, #4338ca 50%, #7e22ce 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  to Grow
                </span>
                <span className="block" style={{
                  background: 'linear-gradient(135deg, #0369a1 0%, #6b21a8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Your Business
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Professional content that understands your brand. <br className="hidden sm:block" />
                <span className="font-semibold text-blue-600">created, scheduled, and published</span> on autopilot.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-center lg:items-start justify-center lg:justify-start">
                <Link to="/register">
                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative"
                  >
                    <div className="absolute -inset-1 rounded-2xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-300" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}></div>
                    <Button
                      size="lg"
                      className="relative text-white px-8 py-6 text-lg font-bold shadow-xl hover:shadow-2xl rounded-2xl border-0 transition-all duration-300"
                      style={{
                        background: 'linear-gradient(to right, rgba(48, 179, 221, 0.9), rgba(74, 41, 138, 0.9))'
                      }}
                    >
                      <span className="flex items-center">
                        Get Started Free
                        <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </span>
                    </Button>
                  </motion.div>
                </Link>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 justify-center lg:justify-start">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">Start in 30 seconds</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium">Cancel anytime</span>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Modern Bento Grid with Videos & Images */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              {/* Modern Bento Grid Layout */}
              <div className="relative w-full h-[600px] grid grid-cols-6 grid-rows-6 gap-3">

                {/* Large Video - Top Left */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="col-span-3 row-span-3 rounded-2xl overflow-hidden shadow-2xl relative group"
                >
                  <video
                    key="video1"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  >
                    <source src="/videos/video1.mp4?v=1" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Image Grid - Top Right */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="col-span-3 row-span-2 rounded-2xl overflow-hidden shadow-xl relative group"
                >
                  <img
                    src="/images/landing_collage/img18.png"
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Small Video - Middle Right */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="col-span-2 row-span-2 rounded-2xl overflow-hidden shadow-xl relative group"
                >
                  <video
                    key="video2"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  >
                    <source src="/videos/video3.mp4?v=2" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Tall Image - Middle Right */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="col-span-1 row-span-2 rounded-2xl overflow-hidden shadow-xl relative group"
                >
                  <img
                    src="/images/landing_collage/img4.png"
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Wide Image - Bottom Left */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="col-span-2 row-span-2 rounded-2xl overflow-hidden shadow-xl relative group"
                >
                  <img
                    src="/images/landing_collage/img26.png"
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Medium Video - Bottom Center */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="col-span-2 row-span-3 rounded-2xl overflow-hidden shadow-2xl relative group"
                >
                  <video
                    key="video3"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  >
                    <source src="/videos/video2.mp4?v=3" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Image Grid - Bottom Right */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  className="col-span-2 row-span-2 rounded-2xl overflow-hidden shadow-xl relative group"
                >
                  <img
                    src="/images/landing_collage/img3.png"
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Small Image - Bottom Far Left */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  className="col-span-1 row-span-1 rounded-2xl overflow-hidden shadow-lg relative group"
                >
                  <img
                    src="/images/landing_collage/img3.jpeg"
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Additional Small Image - Next to img3 */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.05 }}
                  className="col-span-1 row-span-1 rounded-2xl overflow-hidden shadow-lg relative group"
                >
                  <img
                    src="/images/landing_collage/img22.png"
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

                {/* Small Video - Bottom Corner */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.1 }}
                  className="col-span-2 row-span-1 rounded-2xl overflow-hidden shadow-lg relative group"
                >
                  <video
                    key="video4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  >
                    <source src="/videos/video4.mp4?v=4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-rose-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>

              </div>
            </motion.div>

            {/* Mobile Visual - Modern Grid with Videos (Matches Desktop) */}
            <div className="lg:hidden relative h-[500px] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-6 grid-rows-6 gap-2 h-full p-2">
                {/* Large Video - Top Left */}
                <div className="col-span-3 row-span-3 rounded-xl overflow-hidden shadow-lg">
                  <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                    <source src="/videos/video1.mp4?v=1" type="video/mp4" />
                  </video>
                </div>
                {/* Image - Top Right */}
                <div className="col-span-3 row-span-2 rounded-xl overflow-hidden shadow-md">
                  <img src="/images/landing_collage/img18.png" alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                {/* Small Video - Middle Right */}
                <div className="col-span-2 row-span-2 rounded-xl overflow-hidden shadow-md">
                  <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                    <source src="/videos/video2.mp4?v=2" type="video/mp4" />
                  </video>
                </div>
                {/* Tall Image - Middle Right */}
                <div className="col-span-1 row-span-2 rounded-xl overflow-hidden shadow-md">
                  <img src="/images/landing_collage/img4.png" alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                {/* Wide Image - Bottom Left */}
                <div className="col-span-2 row-span-2 rounded-xl overflow-hidden shadow-md">
                  <img src="/images/landing_collage/img22.png" alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                {/* Medium Video - Bottom Center */}
                <div className="col-span-2 row-span-3 rounded-xl overflow-hidden shadow-lg">
                  <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                    <source src="/videos/video3.mp4?v=3" type="video/mp4" />
                  </video>
                </div>
                {/* Image - Bottom Right */}
                <div className="col-span-2 row-span-2 rounded-xl overflow-hidden shadow-md">
                  <img src="/images/landing_collage/img19.jpeg" alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                {/* Small Image - Bottom Far Left */}
                <div className="col-span-1 row-span-1 rounded-xl overflow-hidden shadow-sm">
                  <img src="/images/landing_collage/img3.jpeg" alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                {/* Additional Small Image */}
                <div className="col-span-1 row-span-1 rounded-xl overflow-hidden shadow-sm">
                  <img src="/images/landing_collage/img11.png" alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                {/* Small Video - Bottom Corner */}
                <div className="col-span-2 row-span-1 rounded-xl overflow-hidden shadow-sm">
                  <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                    <source src="/videos/video4.mp4?v=4" type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Who It's For Section - Enhanced Modern Design */}
      <section className={`relative ${isMobile ? 'py-4' : 'py-20'} bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden`}>
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
          <div className="absolute bottom-32 right-20 w-40 h-40 bg-gradient-to-br from-emerald-400/15 to-cyan-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
          <div className="absolute top-60 right-40 w-28 h-28 bg-gradient-to-br from-purple-500/25 to-pink-500/25 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s', animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Structured Data for SEO */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "name": "Quixocial Social Media Management Platform",
                "description": "Quixocial is an AI-powered social media management platform that helps businesses, agencies, and creators generate, schedule, and analyze social media content across multiple platforms.",
                "provider": {
                  "@type": "Organization",
                  "name": "Quixocial"
                },
                "serviceType": "Social Media Management Software",
                "areaServed": "Worldwide"
              })
            }}
          />

          {/* What is Quixocial - Main Header */}
          <div className="text-center mb-20">
            <motion.h2
              className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-gray-900">What is </span>
              <span style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Quixocial?
              </span>
            </motion.h2>

            <motion.p
              className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <strong>Quixocial is your AI-powered social media studio</strong> — streamlining creation, scheduling, and analytics so your business can grow effortlessly. No more tool-hopping or guesswork — just consistent, on-brand content that creates itself and performs beautifully.
            </motion.p>

            {/* Benefits Grid - Ultra Modern Design */}
            <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 ${isMobile ? 'mb-4' : 'mb-20'}`}>
              {[
                {
                  icon: <Zap className="w-8 h-8 text-white" />,
                  gradient: "from-blue-500 via-blue-600 to-purple-600",
                  bgGradient: "from-blue-500/5 via-blue-600/5 to-purple-600/5",
                  borderGradient: "from-blue-400/30 via-blue-500/30 to-purple-500/30",
                  title: "10× Faster",
                  subtitle: "Content Creation",
                  description: "Go from idea to post in seconds. Generate polished, professional content tailored to your voice — instantly",
                  metric: "90% time saved"
                },
                {
                  icon: <BarChart3 className="w-8 h-8 text-white" />,
                  gradient: "from-emerald-500 via-green-500 to-cyan-600",
                  bgGradient: "from-emerald-500/5 via-green-500/5 to-cyan-600/5",
                  borderGradient: "from-emerald-400/30 via-green-500/30 to-cyan-500/30",
                  title: "Complete",
                  subtitle: "Automation",
                  description: "Create once, and Quixocial schedules, publishes, and tracks your content — effortlessly",
                  metric: "Set & forget workflow"
                },
                {
                  icon: <Wand2 className="w-8 h-8 text-white" />,
                  gradient: "from-purple-500 via-pink-500 to-rose-600",
                  bgGradient: "from-purple-500/5 via-pink-500/5 to-rose-600/5",
                  borderGradient: "from-purple-400/30 via-pink-500/30 to-rose-500/30",
                  title: "Brand-Aligned",
                  subtitle: "Content Creation",
                  description: "Our AI learns your tone, visuals, and values — ensuring every post looks and sounds exactly like you",
                  metric: "100% brand aligned"
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="group relative"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                  whileHover={{ y: -12, scale: 1.03 }}
                >
                  {/* Outer border effect */}
                  <div
                    className={`absolute -inset-0.5 rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-700 ${benefit.title === "10× Faster" ? '' : `bg-gradient-to-br ${benefit.gradient}`}`}
                    style={benefit.title === "10× Faster" ? { background: 'linear-gradient(135deg, #30b3dd, #4a298a, #4a298a)' } : {}}
                  ></div>

                  {/* Main card */}
                  <div className={`relative bg-white rounded-3xl p-8 border border-gray-200/50 group-hover:border-transparent shadow-2xl group-hover:shadow-3xl transition-all duration-700 overflow-hidden`}>
                    {/* Animated background gradient */}
                    <div
                      className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ${benefit.title === "10× Faster" ? '' : `bg-gradient-to-br ${benefit.bgGradient}`}`}
                      style={benefit.title === "10× Faster" ? { background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.05), rgba(74, 41, 138, 0.05), rgba(74, 41, 138, 0.05))' } : {}}
                    ></div>

                    {/* Floating orbs */}
                    <div className="absolute top-4 right-4 w-16 h-16 bg-gradient-to-br from-white/20 to-white/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                    <div className="absolute bottom-6 left-6 w-12 h-12 bg-gradient-to-br from-white/15 to-white/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                    <div className="relative z-10 text-center">
                      {/* Icon with enhanced styling */}
                      <motion.div
                        className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 ${benefit.title === "10× Faster" ? '' : `bg-gradient-to-br ${benefit.gradient}`}`}
                        style={benefit.title === "10× Faster" ? { background: 'linear-gradient(135deg, #30b3dd, #4a298a, #4a298a)' } : {}}
                        whileHover={{ rotate: 5 }}
                      >
                        {benefit.icon}
                      </motion.div>

                      {/* Title with enhanced typography */}
                      <h4 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-gray-800 transition-colors">
                        {benefit.title}
                      </h4>
                      <h5
                        className={`text-lg font-semibold mb-6 ${benefit.title === "10× Faster" ? '' : `bg-gradient-to-r ${benefit.gradient} bg-clip-text text-transparent`}`}
                        style={benefit.title === "10× Faster" ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' } : {}}
                      >
                        {benefit.subtitle}
                      </h5>

                      {/* Description with better spacing */}
                      <p className="text-gray-600 leading-relaxed mb-8 text-base group-hover:text-gray-700 transition-colors">
                        {benefit.description}
                      </p>

                      {/* Enhanced metric badge */}
                      <motion.div
                        className={`inline-flex items-center justify-center px-6 py-3 rounded-2xl border bg-white shadow-lg group-hover:shadow-xl transition-all duration-500 ${benefit.title === "10× Faster" ? '' : `bg-gradient-to-r ${benefit.bgGradient}`}`}
                        style={benefit.title === "10× Faster" ? { background: 'linear-gradient(to right, rgba(48, 179, 221, 0.05), rgba(74, 41, 138, 0.05))' } : {}}
                        whileHover={{ scale: 1.05 }}
                      >
                        <span className="text-sm font-bold text-gray-800">
                          {benefit.metric}
                        </span>
                      </motion.div>
                    </div>


                  </div>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Complete Pipeline Showcase - Dark Section */}
      <div id="pipeline" className="relative bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800">
        <Suspense fallback={
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-300">Loading pipeline showcase...</p>
            </div>
          </div>
        }>
          <CompletePipelineShowcase />
        </Suspense>
      </div>

      {/* Enhanced Content Creation Section - Light Section */}
      <div id="content-creation" className="relative bg-white">
        <Suspense fallback={
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading content showcase...</p>
            </div>
          </div>
        }>
          <EnhancedContentShowcase />
        </Suspense>
      </div>

      {/* Post Creation Section - Dark Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 overflow-hidden">
        {/* Background Elements - Desktop Only */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          {/* Geometric Patterns */}
          <div className="absolute top-0 left-0 w-full h-full opacity-30">
            <div className="absolute top-20 left-20 w-64 h-64 border border-blue-200/40 rounded-full"></div>
            <div className="absolute top-40 right-32 w-48 h-48 border border-purple-200/40 rounded-full"></div>
            <div className="absolute bottom-32 left-40 w-32 h-32 border border-cyan-200/40 rounded-full"></div>
          </div>

          {/* Gradient Orbs */}
          <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0s', animationDuration: '4s', background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.25), rgba(74, 41, 138, 0.25))' }}></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1))' }}></div>
        </div>

        {/* Mobile Background Blobs - Mobile Only */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none md:hidden">
          {/* Subtle Gradient Blobs */}
          <div className="absolute top-16 left-4 w-24 h-32 rounded-full blur-2xl transform rotate-12 animate-pulse" style={{ animationDelay: '0s', animationDuration: '6s', background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.15), rgba(74, 41, 138, 0.15))' }}></div>
          <div className="absolute top-32 right-6 w-20 h-28 bg-gradient-to-br from-purple-500/12 to-pink-500/12 rounded-full blur-xl transform -rotate-12 animate-pulse" style={{ animationDelay: '2s', animationDuration: '7s' }}></div>
          <div className="absolute bottom-24 left-8 w-28 h-20 rounded-full blur-2xl transform rotate-45 animate-pulse" style={{ animationDelay: '4s', animationDuration: '8s', background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1))' }}></div>
          <div className="absolute bottom-40 right-4 w-16 h-24 bg-gradient-to-br from-emerald-400/12 to-teal-500/12 rounded-full blur-xl transform -rotate-30 animate-pulse" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-40 bg-gradient-to-br from-indigo-400/8 to-purple-500/8 rounded-full blur-3xl transform rotate-90"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{
              color: '#368EC7',
              textShadow: '0 0 20px rgba(54, 142, 199, 0.4), 0 0 40px rgba(54, 142, 199, 0.2)'
            }}>
              Smart Post Creation
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Transform your ideas into platform-optimized content with intelligent automation
            </p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left Side - Features List */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                {
                  icon: "🎯",
                  title: "Multi-Platform Post Creation",
                  description: "Create content optimized for Instagram, X, LinkedIn, Facebook, and TikTok simultaneously",
                  color: "from-emerald-500 to-teal-500"
                },
                {
                  icon: "🧠",
                  title: "Smart Content Detection",
                  description: "Smart algorithms analyze your content and suggest the best platforms and posting strategies",
                  color: "from-amber-500 to-orange-500"
                },
                {
                  icon: "✍️",
                  title: "Brand-Aligned Captions",
                  description: "Generate captions that match your brand voice and purpose across all platforms",
                  color: "from-rose-500 to-pink-500"
                },
                {
                  icon: "#️⃣",
                  title: "Smart Hashtag Generation",
                  description: "Intelligent hashtag research based on your brand, industry, and trending topics",
                  color: "from-cyan-500 to-blue-500"
                },
                {
                  icon: "🚀",
                  title: "One-Click Multi-Platform Posting",
                  description: "Publish to all your Social accounts instantly or schedule for optimal engagement",
                  color: "from-lime-500 to-green-500"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-4 group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white text-xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Right Side - Interactive Demo */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="relative bg-gray-800 rounded-3xl shadow-2xl p-6 border border-gray-700">
                {/* Demo Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Smart Post Creator</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                </div>

                {/* Three Section Layout */}
                <div className="space-y-4">

                  {/* Section 1: Image Upload */}
                  <motion.div
                    className="bg-gray-700 rounded-xl p-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <span className="text-white text-sm">📷</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-200">Image Upload</span>
                          <div className="text-xs text-gray-400">Water Bottle.jpg</div>
                        </div>
                        <motion.div
                          className="w-12 h-12 rounded-lg overflow-hidden border border-gray-500"
                          whileHover={{ scale: 1.05 }}
                        >
                          <img
                            src="/images/landing/post-creation.png"
                            alt="Uploaded product"
                            className="w-full h-full object-cover"
                          />
                        </motion.div>
                      </div>

                      {/* Brand Kit Selection */}
                      <motion.div
                        className="flex items-center space-x-2 p-2 bg-gray-600 rounded-lg"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                      >
                        <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs">🎨</span>
                        </div>
                        <span className="text-xs text-gray-300 flex-1">EcoLife Brand Kit</span>
                        <div className="flex space-x-1">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Section 2: AI Processing */}
                  <motion.div
                    className="bg-gray-700 rounded-xl p-4"
                    initial={{ opacity: 0, y: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1 }}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <motion.div
                        className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <span className="text-white text-sm">🧠</span>
                      </motion.div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-200">Smart Processing</span>
                        <div className="text-xs text-gray-400">Analyzing and optimizing content</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {/* Processing Steps */}
                      {[
                        { label: 'Analyzing image', delay: 1.5 },
                        { label: 'Applying brand kit', delay: 2, isBrandKit: true },
                        { label: 'Generating captions', delay: 2.5 },
                        { label: 'Creating hashtags', delay: 3 },
                        { label: 'Optimizing platforms', delay: 3.5 }
                      ].map((step, index) => (
                        <motion.div
                          key={step.label}
                          className="flex items-center space-x-2"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: step.delay }}
                        >
                          <motion.div
                            className={`w-2 h-2 rounded-full ${step.isBrandKit ? 'bg-indigo-400' : 'bg-green-400'}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.2, delay: step.delay + 0.2 }}
                          />
                          <span className="text-xs text-gray-300">{step.label}</span>
                          {step.isBrandKit && (
                            <motion.div
                              className="ml-auto flex space-x-1"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.3, delay: step.delay + 0.5 }}
                            >
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            </motion.div>
                          )}
                        </motion.div>
                      ))}

                      {/* Progress Bar */}
                      <div className="mt-3 bg-gray-600 rounded-full h-2">
                        <motion.div
                          className="h-2 rounded-full"
                          style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 4, ease: "easeInOut" }}
                        />
                      </div>
                    </div>
                  </motion.div>

                  {/* Section 3: Post Ready */}
                  <motion.div
                    className="bg-gray-700 rounded-xl p-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 4 }}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                        <span className="text-white text-sm">✅</span>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-200">Post Ready</span>
                        <div className="text-xs text-gray-400">Optimized for all platforms</div>
                      </div>
                      {/* Platform Icons */}
                      <div className="flex space-x-1">
                        {['📘', '❌', '💼', '📸'].map((icon, index) => (
                          <motion.div
                            key={index}
                            className="w-6 h-6 bg-gray-600 rounded flex items-center justify-center text-xs"
                            initial={{ scale: 0, rotate: 180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ duration: 0.3, delay: 4.5 + index * 0.1 }}
                          >
                            {icon}
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Caption Preview */}
                      <div className="bg-gray-600 rounded-lg p-3">
                        <div className="text-sm text-gray-200 mb-2">
                          "🌍 Eco-friendly water bottles! Made from 100% recycled materials, designed for the conscious consumer. Join the sustainability revolution! 💚"
                        </div>

                        {/* Hashtags */}
                        <div className="flex flex-wrap gap-1">
                          {['#EcoFriendly', '#Sustainable', '#WaterBottle', '#GreenLiving'].map((tag, index) => (
                            <motion.span
                              key={tag}
                              className="text-xs bg-blue-600 text-blue-100 px-2 py-1 rounded-full"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.2, delay: 5 + index * 0.1 }}
                            >
                              {tag}
                            </motion.span>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-3">
                        <motion.button
                          className="flex-1 text-white py-2 px-4 rounded-lg text-sm font-medium"
                          style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 5.5 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          Post Now
                        </motion.button>
                        <motion.button
                          className="flex-1 bg-gray-600 text-gray-200 py-2 px-4 rounded-lg text-sm font-medium"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 5.7 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          Schedule
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>


            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link to="/register">
              <motion.div
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.98 }}
                className="inline-block"
              >
                <Button
                  size="lg"
                  className="text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-2xl rounded-2xl"
                  style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
                >
                  Start Creating Smart Posts
                  <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Scheduling Showcase Section - Light Section */}
      <section id="scheduling" className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SchedulingShowcase />
        </div>
      </section>

      {/* Analytics Section - Dark Mode */}
      <div id="analytics" className="bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800">
        <AnalyticsSection />
      </div>

      {/* Testimonials Section - Social Proof */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-slate-900 via-purple-960 to-slate-900 overflow-hidden">
        {/* Floating Orbs Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Mobile: 2 orbs */}
          <div className="md:hidden">
            <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500/30 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
            <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
          </div>

          {/* Desktop: 5 orbs */}
          <div className="hidden md:block">
            <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
            <div className="absolute top-40 right-20 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
            <div className="absolute bottom-40 left-40 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
            <div className="absolute bottom-20 right-40 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[32rem] h-[32rem] bg-indigo-500/20 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-6 leading-tight text-white">
              Loved by Creators & Businesses
            </h2>
            <p className="text-base sm:text-2xl text-gray-300 max-w-3xl mx-auto">
              See how Quixocial is transforming social media management
            </p>
          </div>

          {/* Mobile: Horizontal Scrolling Carousel */}
          <TestimonialCarousel />

          {/* Desktop: Grid Layout */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <motion.div
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              <div className="absolute top-6 right-6 text-blue-100 text-4xl font-serif">"</div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Quixocial helped us create 5× more content in less time, which directly increased our sales. It also saved us valuable time by eliminating the need for manual post publishing. It's the definition of working smart, not hard.
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                  A
                </div>
                <div>
                  <div className="font-semibold text-lg text-gray-900">A. El Masry</div>
                  <div className="text-sm text-gray-500">CSO, Battireo</div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 2 */}
            <motion.div
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              <div className="absolute top-6 right-6 text-purple-100 text-4xl font-serif">"</div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                As a small business owner, I was spending ridiculous amounts of time creating content and managing my social media instead of focusing on my actual work. Quixocial lifted a huge part of that burden.
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                  J
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-lg">J. Blanc</div>
                  <div className="text-sm text-gray-500">Founder, Kunst</div>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 3 */}
            <motion.div
              className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              <div className="absolute top-6 right-6 text-green-100 text-4xl font-serif">"</div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                Quixocial cut our visual content costs, saved us hours on manual publishing and management, and streamlined our entire workflow. Honeslty, it is a game changer!
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                  A
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-lg">Dr. A. Samy</div>
                  <div className="text-sm text-gray-500">Co-Founder, Bebek</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/register">
              <div className="inline-block">
                <Button
                  size="sm"
                  className="relative text-white px-8 py-5 sm:py-6 text-lg sm:text-xl font-bold shadow-xl hover:shadow-2xl rounded-2xl overflow-hidden border-0 transition-all duration-300 backdrop-blur-sm hover:scale-105"
                  style={{
                    background: 'linear-gradient(to right, rgba(48, 179, 221, 0.9), rgba(74, 41, 138, 0.9))',
                    willChange: 'transform'
                  }}
                >
                  <span className="relative z-10 flex items-center">
                    Join Our Success Stories
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </span>
                </Button>
              </div>
            </Link>
          </div>
        </div>
      </section>



      {/* Built for Teams Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
          <div className="absolute bottom-32 right-20 w-40 h-40 bg-gradient-to-br from-emerald-400/15 to-cyan-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
          <div className="absolute top-60 right-40 w-28 h-28 bg-gradient-to-br from-purple-500/25 to-pink-500/25 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s', animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h2 className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#368EC7' }}>
              Built for teams that scale
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From solo creators to enterprise teams — professional content creation and management that grows with you
            </p>
          </motion.div>

          {/* Enhanced Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* Growing Businesses Card */}
            <motion.div
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -8 }}
            >
              <div className="relative glass-card-light rounded-3xl p-8 lg:p-10 shadow-modern-lg modern-card overflow-hidden">
                {/* Gradient Border Effect */}
                <div className="absolute inset-0 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.1), transparent, rgba(74, 41, 138, 0.1))' }}></div>
                <div className="absolute inset-[1px] bg-white/90 backdrop-blur-sm rounded-3xl"></div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Header with Icon */}
                  <div className="flex items-start gap-4 mb-8">
                    <motion.div
                      className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)' }}
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Zap className="w-8 h-8 text-white" />
                    </motion.div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                        Growing Businesses
                      </h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Professional content creation without the overhead
                      </p>
                      <div className="mt-2 text-sm font-semibold text-blue-600">
                        Replace $2,000/month design costs • Manage social media without a team
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4">
                    {[
                      { text: 'Professional content without design experience', icon: '✨' },
                      { text: 'Cost-effective alternative to hiring agencies', icon: '💰' },
                      { text: 'Auto-posting & smart scheduling across platforms', icon: '📅' },
                      { text: 'Real-time analytics & performance insights', icon: '📊' }
                    ].map((feature, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center gap-4 p-3 rounded-xl bg-blue-50/50 hover:bg-blue-50 transition-colors group/item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                        whileHover={{ x: 4 }}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm group-hover/item:shadow-md transition-shadow" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)' }}>
                          <span className="text-lg">{feature.icon}</span>
                        </div>
                        <span className="text-gray-700 font-medium leading-relaxed group-hover/item:text-gray-900 transition-colors">
                          {feature.text}
                        </span>
                      </motion.div>
                    ))}
                  </div>


                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.05), rgba(74, 41, 138, 0.05))' }}></div>
              </div>
            </motion.div>

            {/* Marketing Agencies Card */}
            <motion.div
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ y: -8 }}
            >
              <div className="relative glass-card-light rounded-3xl p-8 lg:p-10 shadow-modern-lg modern-card overflow-hidden">
                {/* Gradient Border Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-green-600/10 rounded-3xl"></div>
                <div className="absolute inset-[1px] bg-white/90 backdrop-blur-sm rounded-3xl"></div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Header with Icon */}
                  <div className="flex items-start gap-4 mb-8">
                    <motion.div
                      className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Users className="w-8 h-8 text-white" />
                    </motion.div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors">
                        Marketing Agencies
                      </h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Scale client success with unified creative workflows
                      </p>
                      <div className="mt-2 text-sm font-semibold text-emerald-600">
                        Save 20+ hours per client per week • Increase capacity by 300%
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4">
                    {[
                      { text: 'Multi-client dashboard & workspace management', icon: '🏢' },
                      { text: 'Brand kit management per client with auto-sync', icon: '🎨' },
                      { text: 'Team collaboration with role-based permissions', icon: '👥' },
                      { text: 'Bulk scheduling & cross-platform analytics', icon: '📊' }
                    ].map((feature, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center gap-4 p-3 rounded-xl bg-emerald-50/50 hover:bg-emerald-50 transition-colors group/item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                        whileHover={{ x: 4 }}
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center text-white shadow-sm group-hover/item:shadow-md transition-shadow">
                          <span className="text-lg">{feature.icon}</span>
                        </div>
                        <span className="text-gray-700 font-medium leading-relaxed group-hover/item:text-gray-900 transition-colors">
                          {feature.text}
                        </span>
                      </motion.div>
                    ))}
                  </div>


                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="pricing-section" className="py-20 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 overflow-visible">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 overflow-visible">
          <div className="text-center mb-16">
            <motion.h2
              className="hero-title text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
              style={{ color: '#368EC7' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span style={{
                textShadow: '0 0 20px rgba(54, 142, 199, 0.4), 0 0 40px rgba(54, 142, 199, 0.2)'
              }}>Simple, Transparent Pricing</span>
            </motion.h2>
            <motion.p
              className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Choose the perfect plan to scale your social presence
            </motion.p>
          </div>

          {/* Loading State */}
          {(tiersLoading || featuresLoading) && (
            <div className="flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          )}

          {/* Credit System Explanation - Dynamic */}
          <motion.div
            className="mt-12 flex justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <DynamicCreditExplanation maxItems={3} />
          </motion.div>

          {/* Monthly/Yearly Toggle */}
          <motion.div
            className="flex justify-center mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="relative bg-slate-800/60 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-1.5 shadow-xl">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setIsYearly(false)}
                  className={`relative px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${!isYearly
                    ? 'text-white shadow-lg'
                    : 'text-gray-300 hover:text-white'
                    }`}
                  style={!isYearly ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)' } : {}}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsYearly(true)}
                  className={`relative px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${isYearly
                    ? 'text-white shadow-lg'
                    : 'text-gray-300 hover:text-white'
                    }`}
                  style={isYearly ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)' } : {}}
                >
                  <span className="flex items-center space-x-2">
                    <span>Yearly</span>
                    <span className="bg-green-500 text-green-50 px-2 py-0.5 rounded-full text-xs font-bold">
                      Save up to 20%
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Dynamic Pricing Cards */}
          {tiers.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto pt-12 items-stretch">
              {tiers
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((tier, index) => {
                  const isPopular = tier.name === 'pro'; // Show "Best Value" badge on Pro tier
                  const tierFeatures = getTierFeatures(tier.id);

                  return (
                    <motion.div
                      key={tier.id}
                      className={`relative rounded-3xl group cursor-pointer transition-all duration-500 ${isMobile ? 'min-h-[500px]' : 'min-h-[700px]'} flex flex-col h-full ${isPopular
                        ? 'border-2 shadow-2xl z-10'
                        : 'bg-gradient-to-br from-slate-800/60 via-gray-800/60 to-slate-900/60 border border-gray-600/30 shadow-xl z-0'
                        } backdrop-blur-xl hover:shadow-2xl ${isPopular ? 'hover:shadow-blue-500/30' : 'hover:shadow-purple-500/20'}`}
                      style={isPopular ? {
                        background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.4), rgba(74, 41, 138, 0.4), rgba(74, 41, 138, 0.4))',
                        borderColor: 'rgba(48, 179, 221, 0.5)',
                        boxShadow: '0 25px 50px -12px rgba(48, 179, 221, 0.2)'
                      } : {}}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      whileHover={{ y: -8, scale: 1.02 }}
                      style={{ marginTop: '0' }}
                    >
                      {/* Animated Background Gradient */}
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={isPopular ? {
                          background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1), rgba(74, 41, 138, 0.1))'
                        } : {
                          background: 'linear-gradient(135deg, rgba(74, 41, 138, 0.05), rgba(48, 179, 221, 0.05), rgba(48, 179, 221, 0.05))'
                        }}
                      ></div>

                      {/* Floating Orbs */}
                      <div className="absolute top-4 right-4 w-20 h-20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.2), rgba(74, 41, 138, 0.2))' }}></div>
                      <div className="absolute bottom-4 left-4 w-16 h-16 rounded-full blur-lg group-hover:scale-125 transition-transform duration-500" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.15), rgba(74, 41, 138, 0.15))' }}></div>

                      {/* Best Value Badge - Outside the card */}
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-30">
                          <div className="text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-2xl backdrop-blur-sm whitespace-nowrap" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', border: '1px solid rgba(48, 179, 221, 0.5)' }}>
                            <span className="flex items-center space-x-1.5">
                              <Crown className="w-3.5 h-3.5" />
                              <span>Best Value</span>
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="relative z-10 p-7 flex flex-col h-full">
                        {/* Header - Compact */}
                        <div className="text-center mb-6">
                          <div
                            className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3 ${isPopular
                              ? 'shadow-lg'
                              : 'bg-gradient-to-br from-gray-600 to-gray-700 shadow-lg'
                              }`}
                            style={isPopular ? { background: 'linear-gradient(135deg, #30b3dd, #4a298a)', boxShadow: '0 10px 15px -3px rgba(48, 179, 221, 0.3)' } : {}}
                          >
                            {getIconComponent(tier.icon)}
                          </div>
                          <h3 className="text-xl font-bold text-white mb-1">{tier.display_name}</h3>
                          <p className="text-gray-300 text-xs leading-relaxed">{tier.description}</p>
                        </div>

                        {/* Pricing - Compact */}
                        <div className="text-center mb-6">
                          {tier.name === 'free' ? (
                            // For free tier, show "Free" text
                            <div className="flex items-baseline justify-center mb-1">
                              <span className="text-3xl font-black text-white tracking-tight">
                                Free
                              </span>
                            </div>
                          ) : getDisplayPrice(tier) === null ? (
                            // For tiers with no pricing, show "Coming Soon"
                            <div className="text-center">
                              <div className="relative flex items-center justify-center mb-1">
                                <span className="text-2xl font-bold text-blue-400">
                                  Coming Soon
                                </span>
                                <div className="absolute -top-1 -right-6">
                                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                </div>
                              </div>
                              <p className="text-xs text-gray-400">
                                Join waitlist for early access
                              </p>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-baseline justify-center mb-1">
                                <span className="text-3xl font-black text-white tracking-tight">
                                  €{getDisplayPrice(tier)}
                                </span>
                                <span className="text-gray-400 text-sm ml-1 font-medium">
                                  {getPricePeriod(tier)}
                                </span>
                              </div>
                              {isYearly && tier.price_yearly && tier.price_monthly && getYearlySavings(tier) > 0 && (
                                <p className="text-xs text-green-400 font-medium">
                                  Save {getYearlySavings(tier)}% vs monthly
                                </p>
                              )}
                            </>
                          )}
                        </div>



                        {/* Credits Section - Show for all tiers including Enterprise */}
                        <div className="mb-6">
                          <div
                            className={`relative p-4 rounded-2xl border backdrop-blur-sm overflow-hidden ${isPopular
                              ? 'border-opacity-40'
                              : 'bg-gradient-to-br from-white/8 to-white/4 border-gray-600/40'
                              }`}
                            style={isPopular ? {
                              background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.15), rgba(74, 41, 138, 0.15))',
                              borderColor: 'rgba(48, 179, 221, 0.4)'
                            } : {}}
                          >

                            {/* Background decoration */}
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-xl"></div>

                            <div className="relative z-10">
                              {/* Credit Display */}
                              <div className="flex items-center justify-center mb-2">
                                <Coins className="w-5 h-5 text-yellow-400 mr-2" />
                                <div className="text-center">
                                  {(() => {
                                    const signupCredits = tier.signup_credits || 0;
                                    const monthlyCredits = tier.monthly_credits || 0;
                                    const bonusCredits = tier.bonus_credits || 0;
                                    const isBonusActive = tierService.isBonusActive(tier);

                                    // For enterprise, show monthly credits directly
                                    if (tier.name === 'enterprise' || tier.id === 'enterprise') {
                                      return (
                                        <>
                                          <div className="text-2xl font-bold text-white">
                                            {monthlyCredits || 5000}
                                          </div>
                                          <div className="text-xs text-gray-300 -mt-1">credits monthly</div>
                                        </>
                                      );
                                    }

                                    // If signup equals monthly and no bonus, show simplified format
                                    if (signupCredits === monthlyCredits && (!isBonusActive || bonusCredits === 0)) {
                                      return (
                                        <>
                                          <div className="text-2xl font-bold text-white">
                                            {monthlyCredits}
                                          </div>
                                          <div className="text-xs text-gray-300 -mt-1">credits monthly</div>
                                        </>
                                      );
                                    }

                                    // Otherwise show the detailed format
                                    return (
                                      <>
                                        <div className="text-2xl font-bold text-white">
                                          {isBonusActive ? bonusCredits : signupCredits}
                                        </div>
                                        <div className="text-xs text-gray-300 -mt-1">credits to start</div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* Monthly Credits (only show if different from signup or bonus is active) */}
                              {(() => {
                                const signupCredits = tier.signup_credits || 0;
                                const monthlyCredits = tier.monthly_credits || 0;
                                const bonusCredits = tier.bonus_credits || 0;
                                const isBonusActive = tierService.isBonusActive(tier);

                                // Don't show for enterprise (already shown above)
                                if (tier.name === 'enterprise' || tier.id === 'enterprise') {
                                  return null;
                                }

                                // Show monthly credits if they differ from signup or if bonus is active
                                if (signupCredits !== monthlyCredits || (isBonusActive && bonusCredits > 0)) {
                                  return (
                                    <div className="text-center text-sm text-gray-400 mb-3">
                                      Renews at {monthlyCredits} credits monthly
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              {/* Bonus Badge (only if bonus is active) */}
                              {tierService.isBonusActive(tier) && tier.bonus_credits && tier.bonus_credits > 0 && (
                                <div className="flex items-center justify-center">
                                  <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-400/30 rounded-full px-3 py-1 flex items-center space-x-1">
                                    <span className="text-xs">🎁</span>
                                    <span className="text-xs font-semibold text-orange-300">
                                      launch bonus
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Features List - Compact with ROI on Hover */}
                        <div className="mb-6 relative">
                          {/* Default Features List */}
                          <div className={`transition-all duration-300 mb-6 ${tier.show_roi_texts && (tier.roi_text_1 || tier.roi_text_2)
                            ? 'group-hover:opacity-0 group-hover:invisible'
                            : ''
                            }`}>
                            <ul className="space-y-3">
                              {(() => {
                                const { displayFeatures, remainingCount } = getLimitedTierFeatures(tier.id);
                                return (
                                  <>
                                    {displayFeatures.map((feature, featureIndex) => (
                                      <li key={featureIndex} className="flex items-start">
                                        <div className="flex-shrink-0 mr-3 mt-0.5">
                                          <Check className="w-4 h-4 text-green-400" />
                                        </div>
                                        <div className="flex-1">
                                          <span className="text-sm leading-relaxed text-gray-300">
                                            {feature.text}
                                            {feature.badgeText && (
                                              <span className={`inline-flex items-center px-2 py-0.5 ml-2 rounded-full text-xs font-medium ${feature.badgeColor === 'green' ? 'bg-green-100 text-green-800' :
                                                feature.badgeColor === 'orange' ? 'bg-orange-100 text-orange-800' :
                                                  feature.badgeColor === 'red' ? 'bg-red-100 text-red-800' :
                                                    feature.badgeColor === 'purple' ? 'bg-purple-100 text-purple-800' :
                                                      feature.badgeColor === 'gray' ? 'bg-gray-100 text-gray-800' :
                                                        'bg-blue-100 text-blue-800'
                                                }`}>
                                                {feature.badgeText}
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      </li>
                                    ))}

                                    {/* Show "+ X more features" if there are remaining features */}
                                    {remainingCount > 0 && (
                                      <li className="flex items-start">
                                        <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-0.5 bg-gradient-to-r from-gray-500 to-gray-600">
                                          <span className="text-white text-xs font-bold">+</span>
                                        </div>
                                        <div className="flex-1">
                                          <Link
                                            to="/pricing"
                                            className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
                                          >
                                            + {remainingCount} more feature{remainingCount !== 1 ? 's' : ''}
                                          </Link>
                                        </div>
                                      </li>
                                    )}
                                  </>
                                );
                              })()}
                            </ul>
                          </div>

                          {/* ROI Messages on Hover - Glassmorphism */}
                          {tier.show_roi_texts && (tier.roi_text_1 || tier.roi_text_2) && (
                            <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-500 flex flex-col justify-center space-y-4 transform scale-95 group-hover:scale-100">
                              {tier.roi_text_1 && (
                                <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl border shadow-xl bg-gradient-to-br from-emerald-500/15 via-green-600/10 to-teal-500/15 border-emerald-400/40">
                                  {/* Background decorations */}
                                  <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-br from-emerald-400/30 to-transparent rounded-full blur-xl"></div>
                                  <div className="absolute bottom-0 left-0 w-8 h-8 bg-gradient-to-br from-green-400/25 to-transparent rounded-full blur-lg"></div>

                                  <div className="relative z-10 p-4">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <span className="text-lg">💰</span>
                                      </div>
                                      <span className="text-sm font-semibold text-emerald-100 leading-relaxed">
                                        {tier.roi_text_1}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {tier.roi_text_2 && (
                                <div className="relative overflow-hidden rounded-2xl backdrop-blur-xl border shadow-xl bg-gradient-to-br from-green-500/15 via-emerald-600/10 to-teal-500/15 border-green-400/40">
                                  {/* Background decorations */}
                                  <div className="absolute top-0 left-0 w-10 h-10 bg-gradient-to-br from-green-400/30 to-transparent rounded-full blur-xl"></div>
                                  <div className="absolute bottom-0 right-0 w-6 h-6 bg-gradient-to-br from-emerald-400/25 to-transparent rounded-full blur-lg"></div>

                                  <div className="relative z-10 p-4">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <span className="text-lg">📈</span>
                                      </div>
                                      <span className="text-sm font-semibold text-green-100 leading-relaxed">
                                        {tier.roi_text_2}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Spacer to push button to bottom */}
                        <div className="flex-grow"></div>
                      </div>

                      {/* CTA Button - Positioned at bottom */}
                      <div className="absolute bottom-1 left-7 right-7 z-20">
                        {tier.is_button_enabled !== false ? (
                          tier.button_url === 'disabled' ? (
                            <Button
                              disabled
                              className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-gray-400 text-gray-600 cursor-not-allowed"
                            >
                              <span className="flex items-center justify-center space-x-2">
                                <span>{getButtonText(tier)}</span>
                              </span>
                            </Button>
                          ) : tier.button_url === 'waitlist' ? (
                            <Button
                              onClick={(e) => handleButtonClick(tier, e)}
                              className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${isPopular
                                ? 'text-white shadow-lg hover:shadow-xl'
                                : tier.name === 'free'
                                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30'
                                  : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white shadow-lg'
                                }`}
                              style={isPopular ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)' } : {}}
                              onMouseEnter={isPopular ? (e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)' : undefined}
                              onMouseLeave={isPopular ? (e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)' : undefined}
                            >
                              <span className="flex items-center justify-center space-x-2">
                                <span>{getButtonText(tier)}</span>
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                              </span>
                            </Button>
                          ) : (
                            <Link to={getButtonLink(tier)}>
                              <Button
                                className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${isPopular
                                  ? 'text-white shadow-lg hover:shadow-xl'
                                  : tier.name === 'free'
                                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30'
                                    : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white shadow-lg'
                                  }`}
                                style={isPopular ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)' } : {}}
                                onMouseEnter={isPopular ? (e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)' : undefined}
                                onMouseLeave={isPopular ? (e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)' : undefined}
                              >
                                <span className="flex items-center justify-center space-x-2">
                                  <span>{getButtonText(tier)}</span>
                                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                              </Button>
                            </Link>
                          )
                        ) : (
                          <Button
                            disabled
                            className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-gray-400 text-gray-600 cursor-not-allowed"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <span>{tier.button_text || 'Not Available'}</span>
                            </span>
                          </Button>
                        )}

                        {/* Additional Info - Reserve space for all tiers */}
                        <div className="h-6 flex items-center justify-center mt-2">
                          {tier.name === 'free' && (
                            <p className="text-center text-xs text-gray-400">
                              No credit card required
                            </p>
                          )}
                          {tier.name === 'enterprise' && (
                            <p className="text-center text-xs text-gray-400">
                              Custom solutions available
                            </p>
                          )}
                        </div>
                      </div>


                    </motion.div>
                  );
                })}
            </div>
          )}

          {/* Additional Info */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-gray-400 mb-4">No credit card required • Start creating today</p>
            <Link to="/pricing" className="text-blue-400 hover:text-blue-300 transition-colors">
              View detailed pricing and features →
            </Link>
          </motion.div>
        </div >
      </section >



      {/* FAQ Section - SEO Optimized */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StructuredFAQ
            faqs={generalFAQs}
            title="Frequently Asked Questions"
            className=""
          />
        </div>
      </section>

      {/* CTA Section - Dark Mode */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="hero-title text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight will-change-transform" style={{
            color: '#368EC7',
            textShadow: '0 0 20px rgba(54, 142, 199, 0.4), 0 0 40px rgba(54, 142, 199, 0.2)'
          }}>
            Ready to scale your content?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed">
            Be among the early adopters shaping the future of social with Quixocial.
          </p>

          <div className="flex justify-center px-4 sm:px-0">
            <Link to="/register" className="w-full sm:w-auto max-w-sm sm:max-w-none">
              <motion.div
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
                className="group relative"
              >
                <div className="absolute -inset-1 rounded-2xl blur opacity-30 group-hover:opacity-70 transition duration-500" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a, #4a298a)' }}></div>
                <Button
                  size="lg"
                  className="relative w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-semibold shadow-lg hover:shadow-2xl rounded-2xl btn-interactive ripple overflow-hidden text-white"
                  style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
                >
                  <span className="relative z-10">Start Free</span>
                  <ArrowRight className="ml-3 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      < footer className="bg-slate-900 text-white" >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="py-12 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">

              {/* Company Info */}
              <div className="lg:col-span-1">
                <div className="mb-6">
                  <img src="/logo-light.png" alt="Quixocial" className="h-12 w-auto block brightness-150 contrast-110" />
                </div>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  Intelligent Quixocial tools for modern creators, marketers, and businesses. Transform your content strategy with AI-powered solutions.
                </p>
                <div className="flex space-x-4">
                  <div className="flex items-center text-sm text-gray-400">
                    <Shield className="w-4 h-4 mr-2 text-green-400" />
                    <span>Enterprise Security</span>
                  </div>
                </div>
              </div>

              {/* Product Links */}
              <div>
                <h3 className="font-semibold text-white mb-6">Product</h3>
                <ul className="space-y-4">
                  <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors duration-200">Pricing</Link></li>
                  <li><Link to="/roadmap" className="text-gray-400 hover:text-white transition-colors duration-200">Roadmap</Link></li>
                  <li><Link to="/resources" className="text-gray-400 hover:text-white transition-colors duration-200">Resources</Link></li>
                </ul>
              </div>

              {/* Company Links */}
              <div>
                <h3 className="font-semibold text-white mb-6">Company</h3>
                <ul className="space-y-4">
                  <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200">About</Link></li>
                  <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors duration-200">Blog</Link></li>
                  <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">Contact</Link></li>
                  <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">Help</Link></li>
                </ul>
              </div>

              {/* Legal & Support */}
              <div>
                <h3 className="font-semibold text-white mb-6">Legal</h3>
                <ul className="space-y-4">
                  <li><Link to="/legal" className="text-gray-400 hover:text-white transition-colors duration-200">All Legal Documents</Link></li>
                  <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">Terms of Service</Link></li>
                  <li><Link to="/data-deletion" className="text-gray-400 hover:text-white transition-colors duration-200">Data Deletion</Link></li>
                  <li><Link to="/cookies" className="text-gray-400 hover:text-white transition-colors duration-200">Cookie Policy</Link></li>
                  <li><Link to="/gdpr-request" className="text-gray-400 hover:text-white transition-colors duration-200">GDPR Request</Link></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-slate-800 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                &copy; 2025 Quixocial. All rights reserved.
              </div>
              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <div className="flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  <span>Global CDN</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer >

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={waitlistModal.isOpen}
        onClose={() => setWaitlistModal({ isOpen: false, tierName: '', featureName: '' })}
        tierName={waitlistModal.tierName}
        featureName={waitlistModal.featureName}
      />
    </div >
  );
};

export default LandingPage;